
import { Phone, ArrowRight, Calendar, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import ContactForm from "@/components/ContactForm";

interface CallToActionProps {
  variant?: "default" | "minimal";
  className?: string;
}

const CallToAction = ({ variant = "default", className = "" }: CallToActionProps) => {
  const [open, setOpen] = useState(false);

  if (variant === "minimal") {
    return (
      <div className={`flex items-center gap-4 ${className}`}>
        <a 
          href="tel:+13237841577" 
          className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
        >
          <Phone className="w-4 h-4" />
          (323) 784-1577
        </a>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="default" size="sm" className="text-sm">
              Contact Us
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <div className="space-y-4">
              <h3 className="text-xl font-bold">Contact Us</h3>
              <p className="text-sm text-muted-foreground">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>
              <ContactForm onSuccess={() => setOpen(false)} />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Default variant
  return (
    <div className={`bg-gradient-to-br from-muted/80 to-muted/30 rounded-lg p-8 shadow-sm border border-border/50 ${className}`}>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
        <div className="max-w-lg">
          <h3 className="text-2xl font-bold mb-3">Ready to improve your search rankings?</h3>
          <p className="text-muted-foreground mb-6">
            Get a free consultation with our SEO experts and discover how we can help your business grow through strategic search engine optimization.
          </p>
          
          <div className="flex flex-wrap gap-3">
            {[
              { text: "Custom Strategy", icon: "✓" },
              { text: "Transparent Pricing", icon: "✓" },
              { text: "ROI Focused", icon: "✓" },
              { text: "No Long Contracts", icon: "✓" }
            ].map((item, i) => (
              <span key={i} className="inline-flex items-center text-sm bg-background/60 px-3 py-1.5 rounded-full border border-border/50">
                <span className="text-primary font-bold mr-1.5">{item.icon}</span> {item.text}
              </span>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <div className="p-5 bg-card rounded-lg border border-border/50 shadow-sm">
            <h4 className="font-semibold mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-primary" />
              Schedule a Free Consultation
            </h4>
            
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="w-full bg-primary-gradient text-white hover:opacity-90">
                  Book Your Free SEO Audit
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <div className="space-y-4">
                  <h3 className="text-xl font-bold">Get Your Free SEO Audit</h3>
                  <p className="text-sm text-muted-foreground">
                    Fill out the form below and we'll analyze your site and prepare a custom strategy.
                  </p>
                  <ContactForm onSuccess={() => setOpen(false)} />
                </div>
              </DialogContent>
            </Dialog>
            
            <div className="mt-3 text-center">
              <span className="text-xs text-muted-foreground">
                No obligation, 100% free strategy session
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-3 justify-center">
            <a 
              href="tel:+13237841577" 
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 font-medium transition-colors hover:bg-indigo-500/20 flex-1"
            >
              <Phone className="w-5 h-5" />
              <span>(323) 784-1577</span>
            </a>
            
            <Link
              to="/contact"
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-teal-500/10 text-teal-600 dark:text-teal-400 font-medium transition-colors hover:bg-teal-500/20 flex-1"
            >
              <MessageCircle className="w-5 h-5" />
              <span>Message Us</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallToAction;
